/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Summary Contract — Conversation summarization endpoints
 *
 * Backs the "Summarize with AI" dialog and the dashboard "Feedbacks &
 * Summaries" tab. The streaming endpoint emits structured-summary partials
 * via SSE (oRPC eventIterator); the save and list endpoints persist /
 * read the result from the `core.conversation_summary` table. Feedback
 * read-only listing is co-located here for the same dashboard surface.
 */
import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";
import { DashboardChannelSchema } from "./dashboard.contract";

// ---------------------------------------------------------------------------
// Schemas (mirrored from ai/tool-output-schemas — kept independent so the
// frontend can import contract types without pulling tool-output module).
// ---------------------------------------------------------------------------

const SummaryReservationSchema = z.object({
	date: z.string(),
	time: z.string().optional(),
	type: z.string().optional(),
	status: z.string().optional(),
	notes: z.string().optional(),
});

const SummaryFeedbackEntrySchema = z.object({
	sentiment: z.enum(["positive", "negative", "neutral"]),
	summary: z.string(),
	quote: z.string().optional(),
});

export const ConversationSummaryPayloadContractSchema = z.object({
	clientName: z.string(),
	phoneE164: z.string().optional(),
	channel: z.string(),
	dateRange: z.object({ from: z.string(), to: z.string() }),
	overview: z.string(),
	reservations: z.array(SummaryReservationSchema),
	feedback: z.array(SummaryFeedbackEntrySchema),
	notes: z.array(z.string()),
	issues: z.array(z.string()),
	metadata: z.object({
		messageCount: z.number().int(),
		generatedAt: z.string(),
	}),
});

export const SummaryStreamChunkSchema = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("partial"),
		// Partial summary as built so far. .passthrough() because keys appear
		// progressively as the LLM streams JSON tokens.
		data: z.object({}).passthrough(),
	}),
	z.object({
		type: z.literal("done"),
		final: ConversationSummaryPayloadContractSchema,
	}),
	z.object({
		type: z.literal("error"),
		errorText: z.string(),
	}),
]);

const StreamSummaryInputSchema = z.object({
	contactPhoneE164: z.string().optional(),
	contactId: z.string().optional(),
	channel: z.string().optional(),
	fromDate: z.string().optional(),
	toDate: z.string().optional(),
	/**
	 * Cap the transcript to the LAST N messages after date/boundary
	 * filtering. Omitted → whole fetched window (legacy behavior; the fetch
	 * itself is hard-capped at 200 messages server-side).
	 */
	messageLimit: z.number().int().min(1).max(200).optional(),
	/**
	 * Explicit transcript boundaries — message IDs (BigInt serialized as
	 * digits-only strings, as returned by the conversation messages
	 * endpoints). When present, the transcript starts at / ends with these
	 * messages inclusive. Unknown IDs are ignored (boundary falls back to
	 * the window edge) so a stale preview can't fail the whole request.
	 */
	firstMessageId: z.string().regex(/^\d+$/).optional(),
	lastMessageId: z.string().regex(/^\d+$/).optional(),
	/**
	 * Operator's preferred output language (IETF code, e.g. "en", "ar").
	 * When omitted, the server falls back to the language configured on the
	 * `summarizeConversation` tool, which itself falls back to the org's
	 * `defaultCountryPrefix` mapping, or English.
	 */
	language: z.string().min(2).max(10).optional(),
});

const SaveSummaryInputSchema = z
	.object({
		contactId: z.string().optional(),
		contactPhoneE164: z.string().optional(),
		channel: z.string().min(1),
		payload: ConversationSummaryPayloadContractSchema,
	})
	.refine((d) => Boolean(d.contactId || d.contactPhoneE164), {
		message: "Either contactId or contactPhoneE164 is required",
	});

const ListSummariesInputSchema = z.object({
	contactId: z.string().optional(),
	channel: DashboardChannelSchema.optional(),
	limit: z.number().int().min(1).max(200).optional(),
	offset: z.number().int().min(0).optional(),
	/**
	 * Optional ISO date (YYYY-MM-DD or any `new Date()`-parseable string).
	 * When provided, only summaries whose `generatedAt` falls within
	 * `[from, to]` (server treats `to` as end-of-day inclusive) are returned.
	 * Both omitted → full set (preserves the pre-filter behavior).
	 */
	from: z.string().max(30).optional(),
	to: z.string().max(30).optional(),
});

const SummaryListItemSchema = z.object({
	id: z.string(),
	contactId: z.string(),
	channel: z.string(),
	generatedAt: z.string(),
	savedById: z.string().nullable(),
	payload: ConversationSummaryPayloadContractSchema,
});

const ListFeedbacksInputSchema = z.object({
	contactId: z.string().optional(),
	channel: DashboardChannelSchema.optional(),
	sentiment: z.enum(["POSITIVE", "NEGATIVE", "NEUTRAL"]).optional(),
	limit: z.number().int().min(1).max(200).optional(),
	offset: z.number().int().min(0).optional(),
	/**
	 * Optional ISO date (YYYY-MM-DD or any `new Date()`-parseable string).
	 * When provided, only feedback rows whose `createdAt` falls within
	 * `[from, to]` (server treats `to` as end-of-day inclusive) are returned.
	 * Both omitted → full set (preserves the pre-filter behavior).
	 */
	from: z.string().max(30).optional(),
	to: z.string().max(30).optional(),
});

const FeedbackListItemSchema = z.object({
	id: z.string(),
	contactId: z.string().nullable(),
	conversationId: z.string().nullable(),
	sourceMessageId: z.string().nullable(),
	channel: z.string(),
	summary: z.string(),
	quote: z.string().nullable(),
	sentiment: z.enum(["POSITIVE", "NEGATIVE", "NEUTRAL"]),
	source: z.enum(["AGENT", "MANUAL"]),
	commentCount: z.number().int().nonnegative(),
	createdAt: z.string(),
});

const FeedbackCommentSchema = z.object({
	id: z.string(),
	authorId: z.string().nullable(),
	authorName: z.string().nullable(),
	authorImage: z.string().nullable(),
	body: z.string(),
	createdAt: z.string(),
});

const AddFeedbackCommentInputSchema = z.object({
	body: z.string().trim().min(1).max(1000),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const summaryContract = {
	stream: oc
		.route({
			method: "POST",
			path: "/api/summary/stream",
			summary: "Stream a structured conversation summary",
			tags: ["Summary"],
		})
		.input(StreamSummaryInputSchema)
		.output(eventIterator(SummaryStreamChunkSchema)),

	save: oc
		.route({
			method: "POST",
			path: "/api/summary",
			summary: "Persist a generated summary",
			tags: ["Summary"],
		})
		.input(SaveSummaryInputSchema)
		.output(z.object({ id: z.string(), generatedAt: z.string() }))
		.errors({ ...commonErrors }),

	list: oc
		.route({
			method: "GET",
			path: "/api/summaries",
			summary: "List saved summaries",
			tags: ["Summary"],
		})
		.input(ListSummariesInputSchema)
		.output(
			z.object({
				items: z.array(SummaryListItemSchema),
				total: z.number().int().nonnegative(),
			})
		),

	listFeedback: oc
		.route({
			method: "GET",
			path: "/api/feedbacks",
			summary: "List captured customer feedback",
			tags: ["Summary"],
		})
		.input(ListFeedbacksInputSchema)
		.output(
			z.object({
				items: z.array(FeedbackListItemSchema),
				total: z.number().int().nonnegative(),
			})
		),
};

export type SummaryContract = typeof summaryContract;
export type SummaryStreamChunk = z.infer<typeof SummaryStreamChunkSchema>;
export type ConversationSummaryPayloadContract = z.infer<
	typeof ConversationSummaryPayloadContractSchema
>;
export type SummaryListItem = z.infer<typeof SummaryListItemSchema>;
export type FeedbackListItem = z.infer<typeof FeedbackListItemSchema>;
export type FeedbackComment = z.infer<typeof FeedbackCommentSchema>;
export type AddFeedbackCommentInput = z.infer<
	typeof AddFeedbackCommentInputSchema
>;
export type StreamSummaryInput = z.infer<typeof StreamSummaryInputSchema>;
export type SaveSummaryInput = z.infer<typeof SaveSummaryInputSchema>;
export type ListSummariesInput = z.infer<typeof ListSummariesInputSchema>;
export type ListFeedbacksInput = z.infer<typeof ListFeedbacksInputSchema>;
